/**
 * DeviceFramePreview — unified device-mockup chrome.
 *
 * The ONE component every "preview this content on a device" surface
 * should wrap its content in. Before this existed, three surfaces
 * reinvented the chrome (page builder had a polished phone mockup
 * with Phone/Tablet/Desktop tabs + notch + URL bar + "Made with
 * PlugD" footer; composer post preview had its own inline notch +
 * home-bar with mobile/desktop toggle; Studio post preview had a
 * minimal `border-[10px] border-black` rectangle with no tabs).
 * The visual inconsistency was the bug — users saw a premium preview
 * in the page builder but a flat card in AI Studio for the same
 * kind of content.
 *
 * Consumers pass the content-to-preview as ``children`` and choose
 * which chrome features they need via props. Zero opinion on what
 * lives inside the screen — page blocks, platform feed cards, raw
 * HTML iframes all work.
 *
 * Used by:
 *  - ``components/builder/PhonePreview.tsx`` (page builder)
 *  - ``components/common/PostPreviewPanel.tsx`` (Studio + composer
 *    shared generic post preview)
 *  - ``components/composer/PostPreview.tsx`` (per-platform post
 *    preview in /compose/:id)
 *  - ``components/studio/StudioPreviewPanel.tsx`` (inherits via the
 *    other three)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Tablet, Monitor } from 'lucide-react';

export type DeviceType = 'phone' | 'tablet' | 'desktop';

// ---------------------------------------------------------------------------
// Device geometry — single source of truth for bezel/screen dimensions
// ---------------------------------------------------------------------------

interface DeviceGeometry {
  /** Max-width of the outer wrapper (Tailwind class). */
  maxWidth: string;
  /** Inner screen height (CSS value). */
  height: string;
  /** Bezel corner-radius (Tailwind class). */
  frameRadius: string;
  /** Inner screen corner-radius (Tailwind class). */
  screenRadius: string;
  /** Padding between bezel and screen (Tailwind class). */
  framePadding: string;
  /** Whether the device has a top notch (phone/tablet yes, desktop no). */
  hasNotch: boolean;
  /** Width of the notch (Tailwind class). Empty when no notch. */
  notchWidth: string;
  /** Whether the device has an on-screen URL bar (phone/tablet yes). */
  hasUrlBar: boolean;
  /** Whether to render a desktop-style browser chrome bar. */
  hasBrowserChrome: boolean;
  /** Whether to render a desktop stand beneath the frame. */
  hasStand: boolean;
  /** i18n key for the tab label. */
  labelKey: string;
  /** Fallback label when the translation key is missing. */
  labelFallback: string;
  /** Lucide icon for the tab. */
  icon: typeof Smartphone;
}

export const DEVICE_GEOMETRY: Record<DeviceType, DeviceGeometry> = {
  phone: {
    maxWidth: 'max-w-[260px]',
    height: '480px',
    frameRadius: 'rounded-[2rem]',
    screenRadius: 'rounded-[1.6rem]',
    framePadding: 'p-[6px]',
    hasNotch: true,
    notchWidth: 'w-20',
    hasUrlBar: true,
    hasBrowserChrome: false,
    hasStand: false,
    labelKey: 'device_frame_phone',
    labelFallback: 'Phone',
    icon: Smartphone,
  },
  tablet: {
    maxWidth: 'max-w-[380px]',
    height: '520px',
    frameRadius: 'rounded-[1.5rem]',
    screenRadius: 'rounded-[1.2rem]',
    framePadding: 'p-[5px]',
    hasNotch: true,
    notchWidth: 'w-16',
    hasUrlBar: true,
    hasBrowserChrome: false,
    hasStand: false,
    labelKey: 'device_frame_tablet',
    labelFallback: 'Tablet',
    icon: Tablet,
  },
  desktop: {
    maxWidth: 'max-w-[480px]',
    height: '400px',
    frameRadius: 'rounded-t-xl rounded-b-none',
    screenRadius: 'rounded-t-lg rounded-b-none',
    framePadding: 'p-[4px] pb-0',
    hasNotch: false,
    notchWidth: '',
    hasUrlBar: false,
    hasBrowserChrome: true,
    hasStand: true,
    labelKey: 'device_frame_desktop',
    labelFallback: 'Desktop',
    icon: Monitor,
  },
};

// ---------------------------------------------------------------------------
// Device selector — pill-style tabs identical to the page builder
// ---------------------------------------------------------------------------

interface DeviceSelectorProps {
  device: DeviceType;
  onChange: (d: DeviceType) => void;
  /** Restrict which tabs render. Defaults to all three. */
  available?: DeviceType[];
  className?: string;
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  device,
  onChange,
  available = ['phone', 'tablet', 'desktop'],
  className,
}) => {
  const { t } = useTranslation();
  if (available.length <= 1) {
    return null;
  }
  return (
    <div
      className={`flex items-center justify-center gap-1 bg-gray-100 rounded-lg p-1 mb-4 ${className || ''}`.trim()}
      role="tablist"
      aria-label={t('device_frame_select_aria', 'Select preview device')}
    >
      {available.map((d) => {
        const geom = DEVICE_GEOMETRY[d];
        const Icon = geom.icon;
        const active = device === d;
        const labelText = t(geom.labelKey, geom.labelFallback);
        return (
          <button
            key={d}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(d)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              active
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            title={labelText}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{labelText}</span>
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DeviceFramePreview — the main export
// ---------------------------------------------------------------------------

export interface DeviceFramePreviewProps {
  /** Currently selected device. */
  device: DeviceType;
  /**
   * When provided, the device tabs render and drive this setter.
   * Omit to lock the device (e.g. always-phone for a narrow sidebar).
   */
  onDeviceChange?: (d: DeviceType) => void;
  /**
   * Restrict tabs — e.g. TikTok / Instagram Feed are phone-only, so
   * pass ``['phone']`` to hide Tablet/Desktop.
   */
  availableDevices?: DeviceType[];
  /**
   * Optional chip/badge row rendered ABOVE the bezel.
   * Use for platform labels ("Instagram", "Made for Twitter").
   */
  headerChip?: React.ReactNode;
  /**
   * Content of the on-screen URL bar (phone/tablet) and the browser
   * chrome address bar (desktop). E.g. ``"plugd.io/my-page"``.
   * Pass ``null``/``undefined`` to hide the URL bar on phone/tablet.
   */
  urlText?: string | null;
  /**
   * Optional footer rendered INSIDE the screen at the bottom
   * (analogous to "Made with PlugD"). For post previews you can use
   * a char counter, a timestamp, or nothing at all.
   */
  screenFooter?: React.ReactNode;
  /**
   * Optional footer rendered BELOW the device (outside the frame).
   * Useful for char counters, helper text.
   */
  outerFooter?: React.ReactNode;
  /**
   * Background color / CSS applied to the screen area. Pass through
   * platform-specific backgrounds (e.g. TikTok black).
   */
  screenBgColor?: string;
  screenBgStyle?: React.CSSProperties;
  /**
   * Additional classes on the OUTER wrapper (the thing that sits in
   * the parent's layout — tabs + bezel + stand).
   */
  className?: string;
  /**
   * If true, the inner content can be scrolled. If false, pointer
   * events are disabled so the preview isn't accidentally scrolled
   * out of sync with the edit surface behind it.
   */
  scrollable?: boolean;
  children: React.ReactNode;
}

export const DeviceFramePreview: React.FC<DeviceFramePreviewProps> = ({
  device,
  onDeviceChange,
  availableDevices,
  headerChip,
  urlText,
  screenFooter,
  outerFooter,
  screenBgColor,
  screenBgStyle,
  className,
  scrollable = true,
  children,
}) => {
  const available = availableDevices ?? ['phone', 'tablet', 'desktop'];

  // Guard: if the caller restricts availableDevices and the currently
  // selected device is NOT in the list, snap to the first available.
  // Parent owns the state so we never mutate silently — just use a
  // fallback geometry for this render. The parent's useEffect should
  // normalise on the next cycle.
  const effectiveDevice: DeviceType = available.includes(device)
    ? device
    : (available[0] || 'phone');
  const effectiveGeom = DEVICE_GEOMETRY[effectiveDevice];

  const mergedScreenStyle: React.CSSProperties = {
    height: effectiveGeom.height,
    ...(screenBgColor ? { backgroundColor: screenBgColor } : {}),
    ...(screenBgStyle || {}),
  };

  return (
    <div className={`w-full ${className || ''}`.trim()}>
      {/* Device selector tabs */}
      {onDeviceChange && available.length > 1 && (
        <DeviceSelector
          device={effectiveDevice}
          onChange={onDeviceChange}
          available={available}
        />
      )}

      {/* Optional chip row above the device */}
      {headerChip && (
        <div className="mb-2 flex justify-center">{headerChip}</div>
      )}

      {/* Outer wrapper — applies the max-width for the selected device */}
      <div className={`mx-auto ${effectiveGeom.maxWidth} transition-all duration-300`}>
        {/* Bezel */}
        <div
          className={`${effectiveGeom.frameRadius} bg-gray-800 ${effectiveGeom.framePadding} relative shadow-2xl`}
        >
          {/* Notch (phone/tablet) */}
          {effectiveGeom.hasNotch && (
            <div
              className={`absolute top-[6px] left-1/2 -translate-x-1/2 ${effectiveGeom.notchWidth} h-5 bg-gray-800 rounded-b-xl z-10`}
              aria-hidden="true"
            />
          )}

          {/* Browser chrome (desktop) */}
          {effectiveGeom.hasBrowserChrome && (
            <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 rounded-t-lg">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" aria-hidden="true" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" aria-hidden="true" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400" aria-hidden="true" />
              <div className="flex-1 mx-3">
                <div className="bg-gray-600 rounded-md px-3 py-1 flex items-center justify-center">
                  <span className="text-[9px] text-gray-300 truncate">
                    {urlText || ''}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Screen */}
          <div
            className={`${
              effectiveGeom.hasBrowserChrome ? 'rounded-none' : effectiveGeom.screenRadius
            } overflow-hidden flex flex-col relative bg-white`}
            style={mergedScreenStyle}
          >
            {/* On-screen URL bar (phone/tablet) */}
            {effectiveGeom.hasUrlBar && urlText && (
              <div className="bg-gray-50/80 backdrop-blur-sm px-3 py-1.5 border-b border-gray-100/30 flex items-center justify-center pt-5 relative z-[1]">
                <span className="text-[9px] text-gray-400 truncate max-w-[180px]">
                  {urlText}
                </span>
              </div>
            )}

            {/* Scrollable content */}
            <div
              className={`flex-1 ${scrollable ? 'overflow-y-auto' : 'overflow-hidden pointer-events-none'} relative z-[1]`}
            >
              {children}
            </div>

            {/* On-screen footer */}
            {screenFooter && (
              <div className="border-t border-gray-100/30 py-1.5 bg-gray-50/80 backdrop-blur-sm relative z-[1]">
                <div className="text-[9px] text-gray-400 text-center">
                  {screenFooter}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop stand */}
        {effectiveGeom.hasStand && (
          <div className="flex flex-col items-center">
            <div className="w-16 h-6 bg-gray-300 rounded-b-sm" aria-hidden="true" />
            <div className="w-28 h-2 bg-gray-300 rounded-b-lg" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Outer footer (below the device) */}
      {outerFooter && (
        <div className="mt-2 text-center text-[11px] text-slate-400">
          {outerFooter}
        </div>
      )}
    </div>
  );
};

export default DeviceFramePreview;
